import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParcoursList } from "../hooks/useParcoursList";
import { listLieux } from "../api/lieuxApi";
import { toLegacyParcours } from "./toLegacyParcours";
import { filterUpcoming } from "./parcoursVisibility";
import LegacyFrontoffice from "../LegacyFrontoffice";

declare global {
  interface Window {
    PA_DATA?: {
      parcours?: unknown[];
      artists?: unknown[];
      venues?: unknown[];
      communes?: unknown[];
      [k: string]: unknown;
    };
  }
}

/**
 * Renders any legacy mockup page (/ landing, /parcours list, …) with backend
 * data injected into window.PA_DATA. Artists are now plain names attached to
 * each parcours — we derive the global D.artists list from the union of names.
 */
export default function HydratedLegacyPage() {
  const parcoursQ = useParcoursList({ page: 0, size: 100, sort: "createdAt,desc" });
  const lieuxQ = useQuery({
    queryKey: ["lieux", "all-summary"],
    queryFn: () => listLieux({ page: 0, size: 500, sort: "name,asc" }),
  });

  const hydrated = useMemo(() => {
    if (!parcoursQ.data) return false;
    if (!window.PA_DATA) window.PA_DATA = {};

    // Public front-office hides parcours whose dates are all in the past.
    const upcoming = filterUpcoming(parcoursQ.data.content);
    window.PA_DATA.parcours = upcoming.map(toLegacyParcours);

    // Communes lookup for the legacy RegionExplorer
    const seen = new Set<string>();
    const communes: { id: string; name: string; region: string; lonlat: [number, number] }[] = [];
    upcoming.forEach((p) => {
      const name = p.mainCommune;
      const region = p.region;
      if (!name || seen.has(name)) return;
      seen.add(name);
      communes.push({ id: name.toLowerCase(), name, region: region ?? "Belgique", lonlat: [4.55, 50.66] });
    });
    window.PA_DATA.communes = communes;

    // D.artists derived from the union of artists across all upcoming parcours (dedup by id).
    const seenArtists = new Map<string, { id: string; name: string; description: string; photo: string }>();
    upcoming.forEach((p) =>
      (p.artists ?? []).forEach((a) => {
        if (!seenArtists.has(a.id)) {
          seenArtists.set(a.id, {
            id: a.id,
            name: a.name,
            description: a.description ?? "",
            photo: a.photoLink ?? "",
          });
        }
      }),
    );
    window.PA_DATA.artists = Array.from(seenArtists.values()).map((a) => ({
      id: a.id,
      name: a.name,
      discipline: "",
      bio: a.description,
      photo: a.photo,
    }));

    if (lieuxQ.data) {
      window.PA_DATA.venues = lieuxQ.data.content.map((l) => ({
        id: l.id,
        name: l.name,
        commune: l.address?.city ?? "",
        lonlat: [l.address?.longitude ?? 4.55, l.address?.latitude ?? 50.66],
      }));
    }
    return true;
  }, [parcoursQ.data, lieuxQ.data]);

  if (parcoursQ.isLoading && !hydrated) return <Status>Chargement…</Status>;
  if (parcoursQ.isError) return <Status error>Impossible de charger les parcours.</Status>;
  const upcomingCount = (window.PA_DATA?.parcours as unknown[] | undefined)?.length ?? 0;
  if (hydrated && upcomingCount === 0) {
    return <Status>Aucun parcours à venir pour le moment.</Status>;
  }
  return <LegacyFrontoffice />;
}

function Status({ children, error }: { children: React.ReactNode; error?: boolean }) {
  return (
    <div
      style={{
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "var(--font-body)",
        color: error ? "var(--red)" : "var(--ink-mute)",
        fontSize: 14,
      }}
    >
      {children}
    </div>
  );
}
